import { Component, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { getHandoverTemplate } from 'src/app/core/store/selectors/handover-template.selector';

import {
  ProjectAccessListLoad,
  ProjectDeleteImage,
  ProjectImageUpload,
  ProjectsLoad,
  ResourceReset,
  SignatureRoleLoad,
} from '@completion/actions';
import { ProjectImageType, ResourceStatus, ResourceType } from '@completion/enums';
import { HandoverTemplate, SignatureSetupCopyResult } from '@completion/models';
import { getCurrentProject, getResourceState } from '@completion/selectors';
import * as handoverTemplateAction from '../../core/store/actions/handover-template.action';
import { ProjectState } from '../../core/store/reducers/project';
import { MatDialog } from '@angular/material/dialog';
import { DataDialogComponent } from './data-dialog/data-dialog.component';
import { NotificationService, UserAccessService } from '@completion/services';
import { SignatureSetupCopyDialogComponent } from './signature-matrix-template/signature-setup-copy-dialog/signature-setup-copy-dialog.component';
import { AppDialogService } from '../../shared/shared-dialog/dialog.service';

@Component({
  selector: 'app-setup-page',
  templateUrl: './setup-page.component.html',
  styleUrls: ['./setup-page.component.scss'],
  standalone: false,
})
export class SetupPageComponent implements OnDestroy {
  projectName: string;
  projectId: number;
  inProgressCustomerLogo = false;
  inProgressHandoverImage = false;
  selectedType: ProjectImageType;
  private readonly destroy$: Subject<void> = new Subject();

  uploadingFile = false;
  handoverImage: string;
  customerLogo: string;
  handoverTemplate: HandoverTemplate;
  hasSignatureMatrixTemplates = false;
  activeTabIndex = 0;
  tabs = ['dim', 'pcfg', 'pht', 'psmt', 'pua', 'ar', 'mde'];
  isIntegrationEnabled$ = new BehaviorSubject<boolean>(false);
  isMipsPublishingMuted$ = new BehaviorSubject<boolean>(false);
  lastIntegrationAttempt$: Subject<string> = new BehaviorSubject<string>('');
  lastIntegrationSuccess$: Subject<string> = new BehaviorSubject<string>('');

  constructor(
    private readonly store: Store<ProjectState>,
    private readonly snackBar: MatSnackBar,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly appDialog: AppDialogService,
    public readonly userAccess: UserAccessService,
    private readonly notifications: NotificationService,
  ) {
    this.handleTabRouting();

    this.store
      .select(getCurrentProject)
      .pipe(
        filter((currentProject) => !!currentProject),
        takeUntil(this.destroy$),
      )
      .subscribe((project) => {
        this.store.dispatch(new handoverTemplateAction.HandoverTemplateLoad(project.id));
        this.store.dispatch(new ProjectAccessListLoad(project));
        this.projectName = project.name;
        this.projectId = project.id;
        this.handoverImage = project.secureHandoverImageUrl;
        this.customerLogo = project.secureCustomerLogoUrl;
        this.hasSignatureMatrixTemplates = Object.values(project.signatureMatrixTemplates ?? {}).some((templates) => templates.length > 0);

        // emit async values for integration settings

        console.log(project.integrationEnabled);
        this.isIntegrationEnabled$.next(!!project.integrationEnabled);
        this.isMipsPublishingMuted$.next(!!project.mipsPublishingMuted);
        this.lastIntegrationAttempt$.next(project.lastIntegrationAttempt || '');
        this.lastIntegrationSuccess$.next(project.lastIntegrationSuccess || '');

        this.inProgressCustomerLogo = true;
        this.inProgressHandoverImage = true;
      });

    this.store
      .select(getHandoverTemplate)
      .pipe(takeUntil(this.destroy$))
      .subscribe((handoverTemplate) => {
        this.handoverTemplate = handoverTemplate;
      });

    this.uploadingImageResource();

    this.deletingImageResource();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
    this.isIntegrationEnabled$.complete();
    this.isMipsPublishingMuted$.complete();
    this.lastIntegrationAttempt$.complete();
    this.lastIntegrationSuccess$.complete();
    this.store.dispatch(new ResourceReset(ResourceType.ProjectImageUpload));
    this.store.dispatch(new ResourceReset(ResourceType.ProjectImageDelete));
  }

  uploadingImageResource() {
    this.store.pipe(select(getResourceState, ResourceType.ProjectImageUpload), takeUntil(this.destroy$)).subscribe((state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      this.selectedType === ProjectImageType.Handover
        ? (this.inProgressHandoverImage = state.status === ResourceStatus.InProgress)
        : (this.inProgressCustomerLogo = state.status === ResourceStatus.InProgress);

      if (state.lastError) {
        this.snackBar.open(state.lastError, 'OK', null);
      }
    });
  }

  deletingImageResource() {
    this.store.pipe(select(getResourceState, ResourceType.ProjectImageDelete), takeUntil(this.destroy$)).subscribe((state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      this.selectedType === ProjectImageType.Handover
        ? (this.inProgressHandoverImage = state.status === ResourceStatus.InProgress)
        : (this.inProgressCustomerLogo = state.status === ResourceStatus.InProgress);

      if (state.lastError) {
        this.snackBar.open(state.lastError, 'OK', null);
      }
    });
  }

  customerLogoUpload(file: File) {
    this.selectedType = ProjectImageType.CustomerLogo;
    this.store.dispatch(new ProjectImageUpload(ProjectImageType.CustomerLogo, file));
  }

  handoverImageUpload(file: File) {
    this.selectedType = ProjectImageType.Handover;
    this.store.dispatch(new ProjectImageUpload(ProjectImageType.Handover, file));
  }

  customerLogoDelete() {
    this.selectedType = ProjectImageType.CustomerLogo;
    this.store.dispatch(new ProjectDeleteImage(ProjectImageType.CustomerLogo));
  }

  handoverImageDelete() {
    this.selectedType = ProjectImageType.Handover;
    this.store.dispatch(new ProjectDeleteImage(ProjectImageType.Handover));
  }

  private handleTabRouting(): void {
    this.route.fragment
      .pipe(
        map((fragment) => (this.tabs.includes(fragment) ? this.tabs.indexOf(fragment) : 0)),
        takeUntil(this.destroy$),
      )
      .subscribe((tabIndex) => {
        this.activeTabIndex = tabIndex;
      });
  }

  setUrlFragment(tabIndex: number): void {
    if (tabIndex !== this.activeTabIndex) {
      const fragment = this.tabs[tabIndex];

      this.router.navigate([], { fragment });
    }
  }

  openDataDialog() {
    const dialog = this.dialog.open(DataDialogComponent, {
      width: '1100px',
      height: '600px',
      data: { jsonData: '' },
    });

    dialog.afterClosed().subscribe((result) => {
      if (result) {
      }
    });
  }

  openSignatureSetupCopyDialog(): void {
    if (!this.projectId || this.hasSignatureMatrixTemplates) {
      return;
    }

    this.appDialog
      .open<SignatureSetupCopyResult>(SignatureSetupCopyDialogComponent, {
        header: 'Copy signature setup',
        width: '36rem',
        breakpoints: { '640px': 'calc(100vw - 2rem)' },
        data: {
          targetProjectId: this.projectId,
          targetProjectName: this.projectName,
        },
      })
      .subscribe((result: SignatureSetupCopyResult | null) => {
        if (!result) {
          return;
        }

        this.store.dispatch(new ProjectsLoad());
        this.store.dispatch(new SignatureRoleLoad());
        const copied = `${result.createdRoleCount} signature role${result.createdRoleCount === 1 ? '' : 's'} and ${result.createdTemplateCount} template${result.createdTemplateCount === 1 ? '' : 's'} copied.`;
        this.notifications.success(copied, 'Signature setup copied');
      });
  }
}
